import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import {
  TicketIcon,
  PlusIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import SubmitTicketModal from '../../components/tickets/SubmitTicketModal';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'badge-warning', icon: ClockIcon },
  in_progress: { label: 'In Progress', color: 'badge-info', icon: ChatBubbleLeftIcon },
  awaiting_user: { label: 'Awaiting Your Reply', color: 'badge-warning', icon: ExclamationTriangleIcon },
  resolved: { label: 'Resolved', color: 'badge-success', icon: CheckCircleIcon },
  closed: { label: 'Closed', color: 'badge-neutral', icon: CheckCircleIcon }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-surface-500' },
  medium: { label: 'Medium', color: 'text-blue-500' },
  high: { label: 'High', color: 'text-amber-500' },
  urgent: { label: 'Urgent', color: 'text-red-500' }
};

const CATEGORY_LABELS = {
  general: 'General',
  technical: 'Technical',
  billing: 'Billing',
  feature_request: 'Feature Request'
};

export default function MyTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filter, setFilter] = useState('all');
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  
  useEffect(() => {
    loadTickets();
  }, [pagination.page, filter]);
  
  const loadTickets = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: 10 };
      if (filter !== 'all') {
        params.status = filter;
      }
      
      const response = await apiHelpers.getTickets(params);
      setTickets(response.data.tickets);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };
  
  const openTickets = tickets.filter(t => !['closed', 'resolved'].includes(t.status)).length;
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/help')}
            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg"
          >
            <ArrowLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-surface-900 dark:text-white">My Support Tickets</h1>
            <p className="text-surface-600 dark:text-surface-400 mt-1">
              {openTickets > 0 ? `${openTickets} open ticket${openTickets > 1 ? 's' : ''}` : 'No open tickets'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowNewTicketModal(true)}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5" />
          New Ticket
        </button>
      </div>
      
      {/* Filters */}
      <div className="flex gap-2">
        {[
          { value: 'all', label: 'All' },
          { value: 'open', label: 'Open' },
          { value: 'in_progress', label: 'In Progress' },
          { value: 'awaiting_user', label: 'Awaiting Reply' },
          { value: 'resolved', label: 'Resolved' },
          { value: 'closed', label: 'Closed' }
        ].map(f => (
          <button
            key={f.value}
            onClick={() => {
              setFilter(f.value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.value
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      
      {/* Tickets List */}
      <div className="space-y-3">
        {loading ? (
          <div className="card">
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            </div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="card text-center py-12">
            <TicketIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-surface-700 dark:text-surface-300 mb-2">
              No tickets found
            </h3>
            <p className="text-surface-500 dark:text-surface-400 mb-4">
              {filter === 'all' 
                ? "You haven't submitted any support tickets yet"
                : `No tickets with status "${filter.replace('_', ' ')}"`
              }
            </p>
            <button
              onClick={() => setShowNewTicketModal(true)}
              className="btn-primary inline-flex"
            >
              <PlusIcon className="w-5 h-5" />
              Submit Your First Ticket
            </button>
          </div>
        ) : (
          tickets.map(ticket => {
            const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || ClockIcon;
            
            return (
              <button
                key={ticket._id}
                onClick={() => navigate(`/tickets/${ticket._id}`)}
                className="card w-full text-left hover:border-primary-300 dark:hover:border-primary-700 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      {ticket.unreadByUser && (
                        <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />
                      )}
                      <h3 className="font-semibold text-surface-900 dark:text-white truncate">
                        {ticket.subject}
                      </h3>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className={STATUS_CONFIG[ticket.status]?.color}>
                        <StatusIcon className="w-3.5 h-3.5 inline mr-1" />
                        {STATUS_CONFIG[ticket.status]?.label}
                      </span>
                      <span className="badge-neutral">
                        {CATEGORY_LABELS[ticket.category]}
                      </span>
                      <span className={`text-sm ${PRIORITY_CONFIG[ticket.priority]?.color}`}>
                        {PRIORITY_CONFIG[ticket.priority]?.label} Priority
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-surface-500">
                      <span>
                        Ticket #{ticket._id.slice(-6).toUpperCase()}
                      </span>
                      <span>
                        Created {formatDistanceToNow(new Date(ticket.createdAt))} ago
                      </span>
                      {ticket.messageCount > 1 && (
                        <span className="flex items-center gap-1">
                          <ChatBubbleLeftIcon className="w-4 h-4" />
                          {ticket.messageCount} messages
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <ChevronRightIcon className="w-5 h-5 text-surface-400 group-hover:text-primary-500 transition-colors" />
                </div>
              </button>
            );
          })
        )}
      </div>
      
      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-surface-500">
            Page {pagination.page} of {pagination.pages} ({pagination.total} tickets)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn-secondary btn-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      {/* Help Link */}
      <div className="card bg-surface-50 dark:bg-surface-800/50 border-dashed">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QuestionMarkCircleIcon className="w-6 h-6 text-surface-400" />
            <p className="text-surface-600 dark:text-surface-400">
              Need help? Check out our documentation first.
            </p>
          </div>
          <button
            onClick={() => navigate('/help')}
            className="btn-ghost text-sm"
          >
            Browse Help Center
          </button>
        </div>
      </div>
      
      {/* New Ticket Modal */}
      <SubmitTicketModal
        isOpen={showNewTicketModal}
        onClose={() => setShowNewTicketModal(false)}
        onSuccess={(ticket) => {
          setShowNewTicketModal(false);
          toast.success('Ticket created successfully!');
          navigate(`/tickets/${ticket._id}`);
        }}
      />
    </div>
  );
}
